import { Component } from "@angular/core";
import { AppState } from "@app/reducers";
import { moduleBasedComponentPermissionsSelector$, OnDemandPreloadService, sidebarDataSelector$ } from "@app/shared";
import { LoggedInUserModel } from "@app/shared/utils";
import { IApplicationResponse } from "@app/shared/utils/interfaces.utils";
import { loggedInUserData } from "@modules/others";
import { select, Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import { map, filter } from "rxjs/operators";
import { ActivatedRoute, Router, NavigationEnd } from "@angular/router";
import { NavigationLoaderService } from "../../services/navigation-loader.service";

@Component({
  selector: "[app-sidebar]",
  templateUrl: "./app-sidebar.component.html"
})
export class AppSidebar {
  applicationResponse: IApplicationResponse;
  loggedInUserData: LoggedInUserModel;
  childMenus = [];
  parentBasedMenus = [];
  accessibleRoutes$: Observable<any>;
  fromComponent: string = '';

  constructor(
    private store: Store<AppState>, 
    private preloadOnDemandService: OnDemandPreloadService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private navigationLoaderService: NavigationLoaderService
  ) {
    this.combineLatestNgrxStoreData();
  }

  combineLatestNgrxStoreData() {
    combineLatest([
      this.store.select(loggedInUserData),
      this.store.select(moduleBasedComponentPermissionsSelector$)]
    ).subscribe((response) => {
      this.loggedInUserData = new LoggedInUserModel(response[0]);
      this.parentBasedMenus = response[1];
    });
  }

  ngOnInit() {
    // Subscribe to query parameters to check for fromComponent
    this.activatedRoute.queryParams.subscribe(params => {
      this.fromComponent = params['fromComponent'] || '';
    });

    // Subscribe to router events to clear fromComponent when navigating away from customer details
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Only clear fromComponent if navigating to a route that's NOT any customer-related page
      if (!event.url.includes('/customer/manage-customers/view') &&
          !event.url.includes('/customer/manage-customers/customer-address') &&
          !event.url.includes('/customer/manage-customers/customer-view-notes') &&
          !event.url.includes('/customer/manage-customers/stand-by-roster') &&
          !event.url.includes('/customer/manage-customers/customer-verification') &&
          !event.url.includes('/customer/manage-customers/lead-info') &&
          !event.url.includes('/customer/manage-customers/casual-guarding') &&
          !event.url.includes('/customer/counter-sales') &&
          !event.url.includes('/event-management/aa-phone-in') &&
          !event.url.includes('/event-management/create-phone-in')) {
        this.fromComponent = '';
      }
    });

    this.accessibleRoutes$ = this.store.pipe(select(sidebarDataSelector$)).pipe(map((sidebarData) => sidebarData?.['modules']));
  }

  /**
   * Helper function to check if current URL matches navigation URL exactly
   * Prevents false positives like 'annual-network-fee' matching 'annual-network-fees-request'
   */
  private matchesNavigationUrl(currentUrl: string, navigationUrl: string): boolean {
    if (!navigationUrl) {
      return false;
    }
    
    // Normalize URLs: remove trailing slashes and query params
    const normalizedNavUrl = navigationUrl.replace(/\/$/, '');
    const normalizedCurrentUrl = currentUrl.split('?')[0].replace(/\/$/, '');
    
    // Exact match
    if (normalizedCurrentUrl === normalizedNavUrl) {
      return true;
    }
    
    // Match at path boundary: URL must start with navigationUrl followed by / or end of string
    // This ensures 'annual-network-fee' doesn't match 'annual-network-fees-request'
    const escapedNavUrl = normalizedNavUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pathBoundaryRegex = new RegExp(`^${escapedNavUrl}(/|$)`);
    return pathBoundaryRegex.test(normalizedCurrentUrl);
  }

  shouldHighlightMenuItem(menuItem: any): boolean {
    const currentUrl = this.router.url;
    
    // If we're coming from non-customer profile and we're on any customer-related page
    if (this.fromComponent === 'Non Customers' && (
      currentUrl.includes('/customer/manage-customers/view') ||
      currentUrl.includes('/customer/manage-customers/customer-address') ||
      currentUrl.includes('/customer/manage-customers/customer-view-notes') ||
      currentUrl.includes('/customer/manage-customers/stand-by-roster') ||
      currentUrl.includes('/customer/manage-customers/customer-verification') ||
      currentUrl.includes('/customer/manage-customers/lead-info') ||
      currentUrl.includes('/customer/manage-customers/casual-guarding') ||
      currentUrl.includes('/customer/counter-sales') ||
      currentUrl.includes('/event-management/aa-phone-in') ||
      currentUrl.includes('/event-management/create-phone-in')
    )) {
      // Only highlight "Non Customers" when coming from non-customer profile
      return menuItem.navigationName === 'Non Customers';
    }
    
    // If we're coming from regular customers, highlight "Customers"
    if (this.fromComponent === 'Customer' && (
      currentUrl.includes('/customer/manage-customers/view') ||
      currentUrl.includes('/customer/manage-customers/customer-address') ||
      currentUrl.includes('/customer/manage-customers/customer-view-notes') ||
      currentUrl.includes('/customer/manage-customers/stand-by-roster') ||
      currentUrl.includes('/customer/manage-customers/customer-verification') ||
      currentUrl.includes('/customer/manage-customers/lead-info') ||
      currentUrl.includes('/customer/manage-customers/casual-guarding') ||
      currentUrl.includes('/customer/counter-sales') ||
      currentUrl.includes('/event-management/aa-phone-in') ||
      currentUrl.includes('/event-management/create-phone-in')
    )) {
      return menuItem.navigationName === 'Customers';
    }
    
    // Normal behavior - check if the current URL matches the menu item's navigation URL
    // This applies to ALL menu items regardless of fromComponent context
    if (menuItem.navigationUrl) {
      return this.matchesNavigationUrl(currentUrl, menuItem.navigationUrl);
    }
    
    return false;
  }

  shouldHighlightParentMenuItem(module: any): boolean {
    const currentUrl = this.router.url;
    
    // If we're coming from non-customer profile and we're on any customer-related page
    if (this.fromComponent === 'Non Customers' && (
      currentUrl.includes('/customer/manage-customers/view') ||
      currentUrl.includes('/customer/manage-customers/customer-address') ||
      currentUrl.includes('/customer/manage-customers/customer-view-notes') ||
      currentUrl.includes('/customer/manage-customers/stand-by-roster') ||
      currentUrl.includes('/customer/manage-customers/customer-verification') ||
      currentUrl.includes('/customer/manage-customers/lead-info') ||
      currentUrl.includes('/customer/manage-customers/casual-guarding') ||
      currentUrl.includes('/customer/counter-sales') ||
      currentUrl.includes('/event-management/aa-phone-in') ||
      currentUrl.includes('/event-management/create-phone-in')
    )) {
      // Only highlight Customer Management module when coming from non-customers
      return module.moduleName === 'Customer Management';
    }
    
    // If we're coming from regular customers, highlight Customer Management module
    if (this.fromComponent === 'Customer' && (
      currentUrl.includes('/customer/manage-customers/view') ||
      currentUrl.includes('/customer/manage-customers/customer-address') ||
      currentUrl.includes('/customer/manage-customers/customer-view-notes') ||
      currentUrl.includes('/customer/manage-customers/stand-by-roster') ||
      currentUrl.includes('/customer/manage-customers/customer-verification') ||
      currentUrl.includes('/customer/manage-customers/lead-info') ||
      currentUrl.includes('/customer/manage-customers/casual-guarding') ||
      currentUrl.includes('/customer/counter-sales') ||
      currentUrl.includes('/event-management/aa-phone-in') ||
      currentUrl.includes('/event-management/create-phone-in')
    )) {
      // Only highlight Customer Management module when coming from customers
      return module.moduleName === 'Customer Management';
    }
    
    // Normal behavior - check if any child navigation matches the current URL
    // This applies to ALL modules regardless of fromComponent context
    if (module.navigations) {
      const shouldHighlight = module.navigations.some((nav: any) => {
        if (nav.navigationUrl && this.matchesNavigationUrl(currentUrl, nav.navigationUrl)) {
          return true;
        }
        // Check nested navigations
        if (nav.navigations) {
          return nav.navigations.some((subNav: any) => 
            this.matchesNavigationUrl(currentUrl, subNav.navigationUrl)
          );
        }
        return false;
      });
      
      return shouldHighlight;
    }
    
    return false;
  }

  shouldUseRouterLink(menuItem: any): boolean {
    const currentUrl = this.router.url;
    
    // Don't use routerLink for "Customers" when we're coming from non-customer profile and on any customer details page
    if (this.fromComponent === 'Non Customers' && 
        currentUrl.includes('/customer/manage-customers/view') && 
        menuItem.navigationName === 'Customers') {
      return false;
    }
    
    return true;
  }

  async onSidebarModuleSectionClicked(selectedModuleObj, selectedSidebarType: string) {
    let selectedModule = { ...selectedModuleObj };
    
    // Check if we're already on the target route to avoid unnecessary loading
    const currentUrl = this.router.url;
    const targetUrl = selectedModule.navigationUrl;
    
    // Start navigation loading immediately when sidebar is clicked (only for items with navigation URLs)
    // But only if we're not already on the target route
    if (selectedModule.navigationUrl && 
        (selectedSidebarType === 'firstlevelChild' || selectedSidebarType === 'secondlevelChild') &&
        currentUrl !== targetUrl) {
      this.navigationLoaderService.startNavigationLoading();
    }
    
    // If we're already on the target route, don't do anything
    if (currentUrl === targetUrl) {
      return;
    }
    
    // Handle special case: if we're coming from non-customer profile and clicking on "Customers"
    if (selectedSidebarType === 'firstlevelChild' && 
        this.fromComponent === 'Non Customers' && 
        selectedModule.navigationName === 'Customers' &&
        this.router.url.includes('/customer/manage-customers/view')) {
      // Clear the fromComponent and navigate to customers
      this.fromComponent = '';
      this.router.navigate([selectedModule.navigationUrl]);
      return;
    }
    
    // preload lazy loaded modules based on sidebar clicks
    switch (selectedSidebarType) {
      case "firstlevelHeader":
        let moduleName = selectedModule?.moduleName.toLowerCase();
        let preloadTopLevelModuleName;
        switch (true) {
          case moduleName.includes('customer'):
            break;
          case moduleName.includes('technical management'):
            preloadTopLevelModuleName = 'technical-management';
            break;
          case moduleName.includes('it management'):
            preloadTopLevelModuleName = 'user';
            break;
        }
        this.preloadOnDemandService.startPreload(preloadTopLevelModuleName);
        setTimeout(() => {
          selectedModule.navigations.forEach((childNavigationObj) => {
            if (childNavigationObj?.navigationUrl && childNavigationObj?.navigations?.length == 0) {
              let lazyLoadUrl = childNavigationObj.navigationUrl.split('/')[2];
              this.preloadOnDemandService.startPreload(lazyLoadUrl);
            }
          });
        }, 200);
        break;
      case "firstlevelChild":
        // For child navigation items, the routerLink will handle navigation
        // The navigation loader service will handle the loading state
        break;
      case "secondlevelHeader":
        selectedModule.navigations.forEach((nestedChildNavigationObj) => {
          if (nestedChildNavigationObj?.navigationUrl && nestedChildNavigationObj?.navigations?.length == 0) {
            let lazyLoadUrl = nestedChildNavigationObj.navigationUrl.split('/')[2];
            this.preloadOnDemandService.startPreload(lazyLoadUrl);
          }
        });
        break;
      case "secondlevelChild":
        // For second level child navigation items, the routerLink will handle navigation
        // The navigation loader service will handle the loading state
        break;
    }
  }
}
